import { useEffect, useState } from 'react';
import axios from 'axios';
import { Heart, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 25;

export default function AdminDonations() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, total_pages: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchDonations = async () => {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      try {
        const response = await axios.get(`${BACKEND_URL}/api/donations`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { page, page_size: PAGE_SIZE },
        });
        if (!cancelled) setData(response.data);
      } catch (err) {
        console.error('Error fetching donations:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchDonations();
    return () => { cancelled = true; };
  }, [page]);

  const totalDonated = data.items
    .filter((d) => d.payment_status === 'paid')
    .reduce((sum, d) => sum + (d.amount || 0), 0);

  const statusColor = (s) => {
    if (s === 'paid') return 'bg-green-500';
    if (s === 'unpaid' || s === 'initiated') return 'bg-yellow-500';
    return 'bg-gray-500';
  };

  return (
    <div data-testid="admin-donations-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-4xl font-unbounded font-black" data-testid="donations-title">Donations</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-card p-6 border border-border">
          <p className="text-sm font-manrope text-gray-400 mb-2">Total Records</p>
          <p className="text-3xl font-unbounded font-black">{data.total}</p>
        </div>
        <div className="bg-card p-6 border border-border">
          <p className="text-sm font-manrope text-gray-400 mb-2">Paid (this page)</p>
          <p className="text-3xl font-unbounded font-black">
            {data.items.filter((d) => d.payment_status === 'paid').length}
          </p>
        </div>
        <div className="bg-card p-6 border border-border">
          <p className="text-sm font-manrope text-gray-400 mb-2">Page total</p>
          <p className="text-3xl font-unbounded font-black">${totalDonated.toFixed(2)}</p>
        </div>
      </div>

      {loading ? (
        <p className="text-center font-manrope">Loading donations…</p>
      ) : data.items.length === 0 ? (
        <div className="bg-card p-12 border border-border text-center" data-testid="no-donations">
          <Heart size={48} className="mx-auto text-gray-500 mb-4" />
          <p className="font-manrope text-gray-400">No donations yet. Once donors complete a Stripe checkout, they'll appear here.</p>
        </div>
      ) : (
        <>
          <div className="bg-card border border-border overflow-hidden">
            <table className="w-full" data-testid="donations-table">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-4 text-left font-manrope font-bold">Date</th>
                  <th className="px-4 py-4 text-left font-manrope font-bold">Donor</th>
                  <th className="px-4 py-4 text-left font-manrope font-bold">Amount</th>
                  <th className="px-4 py-4 text-left font-manrope font-bold">Frequency</th>
                  <th className="px-4 py-4 text-left font-manrope font-bold">Status</th>
                  <th className="px-4 py-4 text-left font-manrope font-bold">Session</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((d) => (
                  <tr key={d.session_id} className="border-t border-border hover:bg-muted/50" data-testid={`donation-row-${d.session_id}`}>
                    <td className="px-4 py-4 font-manrope text-gray-400 text-sm">
                      {new Date(d.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4 font-manrope">
                      <div className="font-medium">{d.donor_name || 'Anonymous'}</div>
                      <div className="text-xs text-gray-400">{d.donor_email || '—'}</div>
                    </td>
                    <td className="px-4 py-4 font-manrope font-bold">${Number(d.amount).toFixed(2)}</td>
                    <td className="px-4 py-4 font-manrope capitalize">{d.frequency}</td>
                    <td className="px-4 py-4">
                      <span className={`${statusColor(d.payment_status)} text-white px-3 py-1 text-xs font-manrope font-medium uppercase`}>
                        {d.payment_status}
                      </span>
                    </td>
                    <td className="px-4 py-4 font-manrope text-xs text-gray-400 truncate max-w-[200px]" title={d.session_id}>
                      {d.session_id.slice(0, 20)}…
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.total_pages > 1 && (
            <Pagination page={page} totalPages={data.total_pages} onChange={setPage} />
          )}
        </>
      )}
    </div>
  );
}

function Pagination({ page, totalPages, onChange }) {
  return (
    <div className="flex items-center justify-between mt-6" data-testid="pagination">
      <button
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page <= 1}
        className="flex items-center gap-1 px-4 py-2 border border-border font-manrope disabled:opacity-40"
        data-testid="pagination-prev"
      >
        <ChevronLeft size={16} /> Prev
      </button>
      <span className="font-manrope text-gray-400">Page {page} of {totalPages}</span>
      <button
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
        className="flex items-center gap-1 px-4 py-2 border border-border font-manrope disabled:opacity-40"
        data-testid="pagination-next"
      >
        Next <ChevronRight size={16} />
      </button>
    </div>
  );
}
